import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { AuthService } from '../services/auth'
import {
  HomeworkService,
  supportsAttachments,
  type Homework,
  type HomeworkSubmission,
  type HomeworkAttachment,
} from '../services/homework'
import { supabase, supabaseUrl } from '../lib/supabase'
import VideoThumbnail from '../components/VideoThumbnail'
import { VideoService } from '../services/videos'
import type { VideoWithCategory } from '../services/videos'
import submissionIconMark from '../assets/icon/submission-icon-mark.svg'
import HomeworkHeader from '../components/HomeworkHeader'
import HomeworkChat from '../components/HomeworkChat'
import HomeworkFeedbackView from '../components/HomeworkFeedbackView'

const now = () =>
  new Date().toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })

const HomeworkDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [currentUser, setCurrentUser] = useState<{ id: string } | null>(null)
  const [homework, setHomework] = useState<Homework | null>(null)
  const [relatedVideo, setRelatedVideo] = useState<VideoWithCategory | null>(null)
  const [answer, setAnswer] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  // Non-draft submission that should be shown as read-only feedback instead of the form.
  // Set only when the homework is no longer editable; null means show the answer form.
  const [feedbackSubmission, setFeedbackSubmission] = useState<HomeworkSubmission | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSavingDraft, setIsSavingDraft] = useState(false)
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(null)
  // Set after a successful 再提出 so the form (which stays on screen) confirms it went through.
  const [resubmittedAt, setResubmittedAt] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [attachments, setAttachments] = useState<HomeworkAttachment[]>([])
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false)
  const [attachmentError, setAttachmentError] = useState('')

  // Decide what to show for an existing submission: the editable answer form, or the
  // read-only feedback view. School homework stays editable until its deadline (mirrors the
  // grade-homework backend check); everything else — and school past its deadline — is not
  // editable and shows feedback instead (grade/feedback stay blank until graded/reviewed).
  const applySubmissionState = (hw: Homework, existing: HomeworkSubmission | null) => {
    if (!existing) return
    // A draft (途中保存) is not yet submitted: always editable, and the submit button should
    // still read 提出 (not 再提出), so skip the submitted-row logic.
    if (existing.status === 'draft') {
      setAnswer(existing.submitted_answer)
      return
    }
    const beforeDeadline = !hw.deadline_at || new Date() < new Date(hw.deadline_at)
    const editable = hw.type === 'school' && beforeDeadline
    if (!editable) {
      setFeedbackSubmission(existing)
      return
    }
    setIsEditing(true)
    setAnswer(existing.submitted_answer)
  }

  useEffect(() => {
    const init = async () => {
      const user = await AuthService.getCurrentUser()
      if (!user) { navigate('/login'); return }
      setCurrentUser(user)
      if (!id) return
      try {
        const hw = await HomeworkService.getHomeworkById(id)

        const existing = await HomeworkService.getSubmission(user.id, id)
        applySubmissionState(hw, existing)

        setHomework(hw)

        // Attachments are offered for school and entre homework.
        if (supportsAttachments(hw.type)) {
          try {
            setAttachments(await HomeworkService.getSubmissionAttachments(user.id, id))
          } catch (e) { console.error(e) }
        }

        if (hw.related_video_id) {
          try {
            const vid = await VideoService.getVideo(hw.related_video_id)
            if (vid) setRelatedVideo(vid)
          } catch { /* non-critical */ }
        }
      } catch (err) {
        console.error(err)
        setError('宿題の読み込みに失敗しました')
      } finally {
        setIsLoading(false)
      }
    }
    init()
  }, [id, navigate])

  const handleSaveDraft = async () => {
    if (!answer.trim() || !currentUser || !homework || isSavingDraft || isSubmitting) return
    setIsSavingDraft(true)
    setError('')
    try {
      await HomeworkService.saveDraft({
        studentId: currentUser.id,
        homeworkId: homework.id,
        answer: answer.trim(),
      })
      setDraftSavedAt(now())
    } catch (err) {
      console.error(err)
      setError('途中保存に失敗しました。もう一度お試しください。')
    } finally {
      setIsSavingDraft(false)
    }
  }

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0 || !currentUser || !homework) return
    setAttachmentError('')
    setIsUploadingAttachment(true)
    try {
      for (const file of Array.from(files)) {
        const validationError = HomeworkService.validateAttachment(file)
        if (validationError) {
          setAttachmentError(`${file.name}: ${validationError}`)
          continue
        }
        const uploaded = await HomeworkService.uploadSubmissionAttachment({
          studentId: currentUser.id,
          homeworkId: homework.id,
          file,
          uploadedBy: currentUser.id,
        })
        setAttachments(prev => [...prev, uploaded])
      }
    } catch (err) {
      console.error(err)
      // The upload path already turns 413/415/network failures into something actionable
      // — showing a fixed message instead would hide why it failed.
      setAttachmentError(
        err instanceof Error && err.message
          ? `ファイルのアップロードに失敗しました: ${err.message}`
          : 'ファイルのアップロードに失敗しました。もう一度お試しください。'
      )
    } finally {
      setIsUploadingAttachment(false)
      e.target.value = '' // allow re-selecting the same file
    }
  }

  const handleRemoveAttachment = async (attachment: HomeworkAttachment) => {
    setAttachmentError('')
    try {
      await HomeworkService.deleteSubmissionAttachment(attachment)
      setAttachments(prev => prev.filter(a => a.id !== attachment.id))
    } catch (err) {
      console.error(err)
      setAttachmentError('ファイルの削除に失敗しました。')
    }
  }

  // A submission needs either a written answer or at least one attachment — the work
  // can live entirely in the attached files (school homework only offers them).
  const canSubmit = answer.trim().length > 0 || attachments.length > 0

  const handleSubmit = async () => {
    if (!canSubmit || !currentUser || !homework || isSubmitting) return
    // isEditing flips on for a first submission that stays editable, so remember whether
    // this send was a re-submission before the state below is re-derived.
    const wasResubmission = isEditing
    setIsSubmitting(true)
    setError('')
    setResubmittedAt(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const resp = await fetch(`${supabaseUrl}/functions/v1/grade-homework`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token || ''}` },
        body: JSON.stringify({ homeworkId: homework.id, answer: answer.trim() })
      })
      const data = await resp.json()
      if (!data.success) {
        throw new Error(data.error || 'AI採点に失敗しました')
      }
      // Re-load the submission and re-decide the view (same URL): editable school homework
      // stays on the form with the submitted answer; anything else switches to feedback.
      const updated = await HomeworkService.getSubmission(currentUser.id, homework.id)
      applySubmissionState(homework, updated)
      if (wasResubmission) setResubmittedAt(now())
    } catch (err) {
      console.error(err)
      setError('提出に失敗しました。もう一度お試しください。')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-24">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#65c1c8]" />
      </div>
    )
  }

  if (!homework) {
    return <div className="max-w-2xl mx-auto px-4 py-12 text-center text-gray-500">宿題が見つかりません</div>
  }

  // Non-editable submission: show the read-only feedback view at this same URL. Grade and
  // feedback stay blank until the submission is graded and (for school/entre) reviewed.
  if (feedbackSubmission) {
    return <HomeworkFeedbackView submission={feedbackSubmission} />
  }

  return (
    <div className="min-h-screen">
      {/* Everything sits in one white panel; the gradient only frames it */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 pt-4 pb-6">
        <div className="bg-white rounded-2xl shadow-lg px-4 pt-4 pb-5 sm:px-6">
          {/* On lg the title, form and chat fill exactly one screen (header 68px plus the
              paddings around this panel), so 提出 and the chat's 送信 are both visible without
              scrolling, like みらいワーク. The textarea absorbs the difference; a very short
              window falls back to the min-height and scrolls. */}
          <div className="flex flex-col lg:h-[calc(100dvh-9.5rem)] lg:min-h-[480px]">
          {/* Header — same title/details block as the feedback view */}
          <HomeworkHeader homework={homework} onWhite />

          {/* Main two-column layout */}
          <div className="flex flex-col lg:flex-row gap-4 lg:items-stretch lg:flex-1 lg:min-h-0">

            {/* Left: Submission form (larger) */}
            <div className="flex-1 flex flex-col min-w-0 lg:min-h-0">
              {/* Section header — dark text now that the panel behind it is white */}
              <div className="px-1 py-2 flex items-center gap-2">
                <img src={submissionIconMark} alt="" style={{ width: 45, height: 40 }} className="flex-shrink-0" />
                <span className="font-bold text-gray-900 text-base">答案を提出しよう</span>
              </div>
              {/* Form card — outlined instead of shadowed, it's white on white */}
              <div className="flex-1 flex flex-col border border-gray-200 rounded-xl overflow-hidden lg:min-h-0">

              <div className="flex-1 flex flex-col p-3 lg:min-h-0 lg:overflow-y-auto">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-gray-500">提出フォーム</p>
                </div>

                {error && (
                  <div className="bg-red-100 text-red-700 text-xs px-3 py-2 rounded mb-2">{error}</div>
                )}

                <textarea
                  value={answer}
                  onChange={e => { setAnswer(e.target.value); if (draftSavedAt) setDraftSavedAt(null); if (resubmittedAt) setResubmittedAt(null) }}
                  placeholder="ここに答えを書いてください..."
                  disabled={isSubmitting}
                  className="flex-1 resize-none w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#65c1c8] mb-1 disabled:bg-gray-50 disabled:text-gray-600"
                  style={{ minHeight: '120px' }}
                />

                <div className="text-right text-xs text-gray-400 mb-2">
                  {answer.length} 字
                </div>

                {/* Attachments — school and entre homework. The files are part of the
                    answer: grade-homework reads what it can (images, PDF, Word/PowerPoint/
                    Excel text) and grades them alongside the text above, and the teacher or
                    admin checks the result either way. */}
                {supportsAttachments(homework.type) && (
                  <div className="mb-2">

                    {attachments.length > 0 && (
                      <ul className="flex flex-col gap-1.5 mb-2">
                        {attachments.map(att => (
                          <li key={att.id} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                            <span className="text-base">{HomeworkService.getFileIcon(att.mime_type)}</span>
                            <span className="flex-1 min-w-0 truncate text-sm text-gray-700">{att.file_name}</span>
                            <span className="text-xs text-gray-400 whitespace-nowrap">{HomeworkService.formatFileSize(att.file_size)}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveAttachment(att)}
                              disabled={isSubmitting}
                              className="text-gray-400 hover:text-red-500 disabled:opacity-40"
                              aria-label="削除"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}

                    {attachmentError && (
                      <p className="text-xs text-red-600 mb-2">{attachmentError}</p>
                    )}

                    <div className={'flex items-center gap-2 flex-wrap'}>
                      <label className={`inline-flex items-center gap-1.5 shrink-0 text-sm font-semibold text-[#da4574] border border-[#da4574] rounded-lg px-3 py-1.5 cursor-pointer hover:bg-[#da4574]/5 ${isUploadingAttachment || isSubmitting ? 'opacity-40 pointer-events-none' : ''}`}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                        {isUploadingAttachment ? 'アップロード中...' : 'ファイルを添付'}
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.txt"
                          multiple
                          onChange={handleFileSelect}
                          disabled={isUploadingAttachment || isSubmitting}
                          className="hidden"
                        />
                      </label>
                      <p className={`text-xs text-gray-400`}>
                        PDF、Word、画像（1ファイル最大50MB）
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-2">
                  {/* 途中保存 only applies before the first submission; once submitted
                      (isEditing), a re-edit goes straight through 再提出. */}
                  {!isEditing && draftSavedAt && (
                    <p className="text-center text-xs text-gray-500">{draftSavedAt} に途中保存しました</p>
                  )}
                  {resubmittedAt && (
                    <p className="text-center text-xs text-gray-500">{resubmittedAt} に再提出しました</p>
                  )}
                  {/* 横並び。再提出のときは途中保存が無いので提出が全幅になる */}
                  <div className="flex gap-2">
                    {!isEditing && (
                      <button
                        onClick={handleSaveDraft}
                        disabled={!answer.trim() || isSavingDraft || isSubmitting}
                        className="flex-1 py-2 font-bold text-sm rounded-lg border border-[#da4574] text-[#da4574] bg-white disabled:opacity-40 transition-colors"
                      >
                        {isSavingDraft ? '保存中...' : '途中保存'}
                      </button>
                    )}
                    <button
                      onClick={handleSubmit}
                      disabled={!canSubmit || isSubmitting}
                      className="flex-1 py-2 text-white font-bold text-sm rounded-lg disabled:opacity-40 transition-colors"
                      style={{ background: isSubmitting ? '#aaa' : '#da4574' }}
                    >
                      {isSubmitting ? '送信中...' : isEditing ? '再提出' : '提出'}
                    </button>
                  </div>
                </div>
              </div>
              </div>
            </div>

            {/* Right: Chat (smaller). Stretches to the row's height. On lg the chat is taken
                out of flow (absolute), so a long conversation can never stretch the row —
                the messages scroll instead. */}
            <div className="lg:w-80 xl:w-96 flex flex-col flex-shrink-0 min-h-0 h-[60vh] lg:h-auto lg:relative">
              <div className="flex-1 min-h-0 flex flex-col lg:absolute lg:inset-0">
                {currentUser && <HomeworkChat homework={homework} studentId={currentUser.id} onWhite />}
              </div>
            </div>

          </div>
          </div>

          {/* Related video section */}
          {homework.related_video_id && (
            <div className="mt-6">
              <h2 className="text-base font-semibold text-gray-800 mb-3">関連動画</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {relatedVideo ? (
                  <Link to={`/video/${relatedVideo.id}`} className="block group">
                    <div className="bg-white rounded-lg overflow-hidden shadow hover:shadow-md transition-shadow">
                      <VideoThumbnail
                        thumbnailUrl={relatedVideo.thumbnail_url}
                        title={relatedVideo.title}
                        className="w-full aspect-video"
                        thumbnailTitle={relatedVideo.thumbnail_title}
                        schoolOriginal={relatedVideo.is_school_video && relatedVideo.video_type === 'chat_only'}
                      />
                      <div className="p-2">
                        <p className="text-xs font-semibold text-[#da4574] line-clamp-2">{relatedVideo.title}</p>
                      </div>
                    </div>
                  </Link>
                ) : (
                  <Link to={`/video/${homework.related_video_id}`} className="block group">
                    <div className="bg-white rounded-lg overflow-hidden shadow hover:shadow-md transition-shadow">
                      <div className="w-full aspect-video bg-gray-100 flex items-center justify-center">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div className="p-2">
                        <p className="text-xs text-gray-500">関連動画を見る</p>
                      </div>
                    </div>
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default HomeworkDetailPage
